import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { SectionName } from "./useSectionContent";

/**
 * Like useSectionContent, but filters by a `page` field inside `published_data`
 * (used by the `faqs` and `local_areas` sections, which store one row per item
 * tagged with the page they belong to).
 */
export function usePageContent<T extends { id: string; page: string; sort_order?: number }>(
  section: SectionName | "faqs" | "local_areas",
  page: string,
  seed: T[],
): { items: T[]; loading: boolean; source: "db" | "seed" } {
  const seeded = seed.filter((s) => s.page === page);
  const [items, setItems] = useState<T[]>(seeded);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<"db" | "seed">("seed");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("site_content")
        .select("published_data, sort_order")
        .eq("section", section)
        .eq("status", "published")
        .order("sort_order", { ascending: true });

      if (cancelled) return;
      if (error || !data) {
        setItems(seeded);
        setSource("seed");
      } else {
        const filtered = data
          .map((r) => r.published_data as T)
          .filter((d) => d && d.page === page);
        if (filtered.length === 0) {
          setItems(seeded);
          setSource("seed");
        } else {
          setItems(filtered);
          setSource("db");
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [section, page, seed]);

  return { items, loading, source };
}
